import { SHARED_DATA_STATUS } from "@/domain/sharedData/constants"
import type { SharedDataJsonldReadCollectionSharedData } from "@/networking/api"

type Share = SharedDataJsonldReadCollectionSharedData

export type TimelineStepKey = "sent" | "accepted" | "completed"
export type TimelineStep = { key: TimelineStepKey; isDone: boolean; date?: string | null }

// A refusal, a revocation, a cancellation or an abandonment stops the timeline.
export type TimelineStop = { status: string; date?: string | null }

const STOPPING_STATUSES: Record<string, (share: Share) => string | null | undefined> = {
  [SHARED_DATA_STATUS.DENIED]: share => share.deniedAt,
  [SHARED_DATA_STATUS.REVOKED]: share => share.revokedAt,
  [SHARED_DATA_STATUS.CANCELLED]: share => share.terminatedAt ?? share.updatedAt,
  [SHARED_DATA_STATUS.CLOSED]: share => share.terminatedAt ?? share.updatedAt,
}

// Three steps (ADR 0009, D3): Sent, Accepted, Completed. A step is done once the share reached it.
export const getTimeline = (share: Share): { steps: TimelineStep[]; stop?: TimelineStop } => {
  const status = share.status?.code ?? SHARED_DATA_STATUS.ASKED
  const wasAccepted = share.acceptedAt != null
  const isCompleted =
    status === SHARED_DATA_STATUS.COMPLETED ||
    (status === SHARED_DATA_STATUS.ARCHIVE && share.terminatedAt != null)

  const steps: TimelineStep[] = [
    { key: "sent", isDone: true, date: share.createdAt },
    {
      key: "accepted",
      isDone: wasAccepted || status === SHARED_DATA_STATUS.ACCEPTED || isCompleted,
      date: share.acceptedAt,
    },
    { key: "completed", isDone: isCompleted, date: isCompleted ? share.terminatedAt : null },
  ]

  const stopDate = STOPPING_STATUSES[status]
  return stopDate ? { steps, stop: { status, date: stopDate(share) } } : { steps }
}

const CLOSED_INTERBU_STATUSES: string[] = [SHARED_DATA_STATUS.CLOSED, SHARED_DATA_STATUS.COMPLETED]

export type CardActions = { canAnswer: boolean; canCancel: boolean }

// Same rules as the detail modal (ShareResponseModal): only a pending share of an InterBU that is
// neither closed nor completed can be answered (receiving agency's manager) or cancelled (requestor).
export const getCardActions = (
  share: Share,
  { isRecipientManager, isRequestor }: { isRecipientManager: boolean; isRequestor: boolean }
): CardActions => {
  const isPending = share.status?.code === SHARED_DATA_STATUS.ASKED
  const isInterBuOpen = !CLOSED_INTERBU_STATUSES.includes(share.interBu?.status?.code ?? "")

  return {
    canAnswer: isPending && isInterBuOpen && isRecipientManager,
    canCancel: isPending && isInterBuOpen && isRequestor,
  }
}

// "all": both sides at once, the administrators' view.
export type InterBUDirection = "received" | "sent" | "all"

export type CommentSummary = { count: number; lastAt?: string | null; isFromOtherSide: boolean }

// Comments of the share (ADR 0009, D4), from the list's summary fields: highlighted when the last
// comment comes from the other side, i.e. it is yours to read. Both sides (all, the
// administrators' view): no side of one's own, nothing highlighted.
export const getCommentSummary = (share: Share, direction: InterBUDirection): CommentSummary => {
  const count = share.commentCount ?? 0
  if (direction === "all") return { count, lastAt: share.lastCommentAt, isFromOtherSide: false }
  const mySide = direction === "received" ? "recipient" : "sender"
  return {
    count,
    lastAt: share.lastCommentAt,
    isFromOtherSide: count > 0 && share.lastCommentSide != null && share.lastCommentSide !== mySide,
  }
}
